import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import PartnerApi from "../../../helpers/PartnerApi";
import { formatDateByLang } from "../../function/formatDate";

const InfoRow = ({ label, value }) => {
  const showValue = value === null || value === undefined ? "" : String(value);

  return (
    <div className="flex items-start gap-3 min-w-0 flex-wrap transition-colors duration-300">
      <span className="w-32 shrink-0 font-medium text-gray-800 dark:!text-white">
        {label}:
      </span>

      <span className="flex-1 min-w-0 text-gray-700 dark:!text-white break-words whitespace-normal text-left">
        {showValue}
      </span>
    </div>
  );
};

const mapLanguageCode = (code) => {
  switch (String(code)) {
    case "40":
      return "Turkish";
    case "1":
      return "Deutsch";
    case "3":
      return "English";
    case "12":
      return "Nederlands";
    case "5":
      return "Français";
    case "19":
      return "العربية";
    default:
      return "DİĞER";
  }
};

const CustomerDetailList = ({ data }) => {
  const [sectorOptions, setSectorOptions] = useState([]);
  const [countryMap, setCountryMap] = useState({});
  const { i18n, t } = useTranslation();

  useEffect(() => {
    const fetchSectorList = async () => {
      try {
        const response = await PartnerApi.getSectorList({ language: "tr" });

        const options = (response?.sectorList || [])
          .filter((s) => s?.name?.trim())
          .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
          .map((s) => ({
            label: s.name.trim(),
            value: String(s.code ?? "").trim(),
          }));

        setSectorOptions(options);
      } catch (err) {
        console.error("Sector list alınamadı:", err);
      }
    };

    fetchSectorList();
  }, []);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const res = await PartnerApi.getCountryList({
          language: i18n.language || "tr",
          status: 1,
          limit: 500,
          offset: 0,
        });

        const list = Array.isArray(res?.countryList) ? res.countryList : [];

        const map = list.reduce((acc, item) => {
          const code = String(item?.code ?? item?.countryCode ?? "").trim();
          const name = String(item?.name ?? "").trim();
          if (code) acc[code] = name || code;
          return acc;
        }, {});

        setCountryMap(map);
      } catch (err) {
        console.error("Country list alınamadı:", err);
      }
    };

    fetchCountries();
  }, [i18n.language]);

  const getSectorName = (sectorCode) => {
    const code = String(sectorCode ?? "").trim();
    const found = sectorOptions.find((s) => String(s.value) === code);
    return found?.label || "DİĞER";
  };

  // geçersiz/ülke olmayan değerler → boş
  const getCountryName = (countryCode) => {
    const code = String(countryCode ?? "").trim();
    if (!code || code === "-1" || code === "0") return "";
    return countryMap[code] ? countryMap[code] : "";
  };

  const sortedCustomerList = useMemo(() => {
    const base = Array.isArray(data?.customerList) ? data.customerList : [];
    if (sectorOptions.length === 0) return base;

    return base.slice().sort((a, b) => {
      const nameA = getSectorName(a?.sectorCode);
      const nameB = getSectorName(b?.sectorCode);
      return nameA.localeCompare(nameB);
    });
  }, [data, sectorOptions]);

  return (
    <div className="mb-6 w-full">
      {sortedCustomerList.length > 0 ? (
        <div className="flex flex-col gap-4 sm:gap-6 lg:gap-8">
          {sortedCustomerList.map((customer) => {
            const sectorName = getSectorName(customer?.sectorCode);

            return (
              <div
                key={customer?.id}
                className="
              w-full
              rounded-lg
              border border-gray-300
              bg-white text-gray-800
              transition-colors duration-300
              p-4 sm:p-5 lg:p-6
              dark:bg-gray-600 dark:text-white
              dark:border-gray-400
            "
              >
                <h3 className="font-semibold text-base sm:text-lg mb-3 sm:mb-4 break-words text-gray-800 dark:!text-white">
                  {customer?.name ?? "-"}
                </h3>

                {/* ✅ İç alan: küçük ekranda 1 kolon, geniş ekranda 2 kolon */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 text-sm">
                  {/* Sol kolon */}
                  <div className="space-y-2 min-w-0">
                    <InfoRow label={t("Database")} value={customer?.databaseName} />
                    <InfoRow label={t("Database Key")} value={customer?.databaseUniversalKey} />
                    <InfoRow label={t("IP Address")} value={customer?.cusIp} />
                    <InfoRow label={t("Version")} value={customer?.version} />
                    <InfoRow label={t("Language")} value={mapLanguageCode(customer?.language)} />
                    <InfoRow
                      label={t("First Use")}
                      value={formatDateByLang(customer?.firstUseDate, i18n.language)}
                    />
                    <InfoRow
                      label={t("Last Use")}
                      value={formatDateByLang(customer?.lasttUseDate, i18n.language)}
                    />
                    <InfoRow label={t("Sector Code")} value={sectorName} />
                  </div>

                  {/* Sağ kolon */}
                  <div className="space-y-2 min-w-0">
                    <InfoRow label={t("Name")} value={customer?.name} />
                    <InfoRow label={t("City")} value={customer?.city} />
                    <InfoRow label={t("Country")} value={getCountryName(customer?.country)} />
                    <InfoRow label={t("Adress1")} value={customer?.address1} />
                    <InfoRow label={t("Adress2")} value={customer?.address2} />
                    <InfoRow label={t("Phone")} value={customer?.tel} />
                    <InfoRow label={t("Email")} value={customer?.mail} />
                    <InfoRow label={t("Web")} value={customer?.url} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-sm text-gray-600 dark:!text-white">
          {t("No customers found.")}
        </p>
      )}
    </div>

  );
};

export default CustomerDetailList;
